import React from 'react';
import { TrendingUp, MessageSquare, Calendar, RefreshCw } from 'lucide-react';
import { AssistantStats, QuestionAnalysis } from '../../types';
import SampleQuestions from '../SampleQuestions';

interface InsightsPageProps {
    stats: AssistantStats;
    sampleQuestions: string[];
    questionAnalysis?: QuestionAnalysis;
    assistantName: string;
    assistantId: string;
    newQuestionsCount: number;
    onRefreshAnalysis: () => Promise<void>;
}

const InsightsPage: React.FC<InsightsPageProps> = ({
    stats,
    sampleQuestions,
    questionAnalysis,
    assistantName,
    assistantId,
    newQuestionsCount,
    onRefreshAnalysis
}) => {
    const hasConversations = stats.totalConversations > 0;

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold text-slate-900 mb-2">Insights</h1>
                <p className="text-slate-600">Understand how people interact with your Echoo</p>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                    <div className="flex items-center">
                        <div className="p-2 bg-blue-100 rounded-lg mr-4">
                            <MessageSquare className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                            <div className="text-2xl font-bold text-slate-900">{stats.totalConversations}</div>
                            <div className="text-sm text-slate-600">Total Conversations</div>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                    <div className="flex items-center">
                        <div className="p-2 bg-green-100 rounded-lg mr-4">
                            <TrendingUp className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                            <div className="text-2xl font-bold text-slate-900">{stats.totalexchanges}</div>
                            <div className="text-sm text-slate-600">Total Messages</div>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                    <div className="flex items-center">
                        <div className="p-2 bg-purple-100 rounded-lg mr-4">
                            <Calendar className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                            <div className="text-2xl font-bold text-slate-900">
                                {stats.lastConversationDate ?
                                    new Date(stats.lastConversationDate).toLocaleDateString() :
                                    'Never'
                                }
                            </div>
                            <div className="text-sm text-slate-600">Last Activity</div>
                        </div>
                    </div>
                </div>
            </div>

            {!hasConversations ? (
                /* No Data State */
                <div className="bg-white rounded-2xl p-12 text-center border border-slate-200">
                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <TrendingUp className="w-8 h-8 text-slate-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">No conversations yet</h3>
                    <p className="text-slate-600 mb-6 max-w-md mx-auto">
                        Once people start chatting with your Echoo, you'll see insights about their questions and interests here.
                    </p>
                    <div className="text-sm text-slate-500">
                        Share your Echoo URL to start getting conversations!
                    </div>
                </div>
            ) : (
                /* Insights Content */
                <div className="space-y-6">
                    {/* Question Analysis */}
                    {questionAnalysis && (
                        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold text-slate-900">Question Categories</h2>
                                <button
                                    onClick={onRefreshAnalysis}
                                    className="flex items-center px-3 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
                                >
                                    <RefreshCw className="w-4 h-4 mr-1" />
                                    Refresh
                                </button>
                            </div>

                            {questionAnalysis.categories && questionAnalysis.categories.length > 0 ? (
                                <div className="space-y-4">
                                    {questionAnalysis.categories.map((category, index) => (
                                        <div key={index} className="border border-slate-200 rounded-lg p-4">
                                            <div className="flex items-center justify-between mb-2">
                                                <h3 className="font-semibold text-slate-900">{category.category}</h3>
                                                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                                                    {category.count} questions
                                                </span>
                                            </div>
                                            {category.examples && category.examples.length > 0 && (
                                                <div className="space-y-1">
                                                    {category.examples.slice(0, 3).map((example, exampleIndex) => (
                                                        <div key={exampleIndex} className="text-sm text-slate-600 italic">
                                                            "{example}"
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-slate-500">
                                    <p>Question analysis will appear here once you have more conversations.</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Common Themes */}
                    {questionAnalysis?.commonThemes && questionAnalysis.commonThemes.length > 0 && (
                        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
                            <h2 className="text-lg font-semibold text-slate-900 mb-4">Common Themes</h2>
                            <div className="flex flex-wrap gap-2">
                                {questionAnalysis.commonThemes.map((theme, index) => (
                                    <span
                                        key={index}
                                        className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm"
                                    >
                                        {theme}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Insights */}
                    {questionAnalysis?.insights && questionAnalysis.insights.length > 0 && (
                        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
                            <h2 className="text-lg font-semibold text-slate-900 mb-4">Key Insights</h2>
                            <div className="space-y-3">
                                {questionAnalysis.insights.map((insight, index) => (
                                    <div key={index} className="flex items-start">
                                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                                        <p className="text-slate-700">{insight}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Sample Questions */}
            {sampleQuestions && sampleQuestions.length > 0 && (
                <SampleQuestions
                    questions={sampleQuestions}
                    questionAnalysis={questionAnalysis}
                    assistantName={assistantName}
                    assistantId={assistantId}
                    onRefreshAnalysis={onRefreshAnalysis}
                    newQuestionsCount={newQuestionsCount}
                />
            )}
        </div>
    );
};

export default InsightsPage;
